import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import ContactCTA from '../components/ContactCTA';
import { comparisons } from '../data/comparisons';
import { combineSchemas, organizationSchema, breadcrumbSchema } from '../lib/schema';
import { Check, X, Trophy } from 'lucide-react';

const ComparisonPage = () => {
  const { slug } = useParams();
  const c = comparisons[slug];
  if (!c) return <Navigate to="/" replace />;

  const path = `/compare/${c.slug}`;
  const schema = combineSchemas(
    organizationSchema,
    breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Comparisons', path: '/compare' }, { name: c.title, path }]),
  );

  return (
    <Layout>
      <SEO
        title={`${c.title} | Website Symphony`}
        description={c.intro}
        keywords={`custom web design vs ${c.competitor.toLowerCase()}, ${c.competitor.toLowerCase()} alternative, best web design agency, custom website vs template`}
        canonical={path}
        schema={schema}
      />
      <Breadcrumbs items={[{ name: 'Comparisons', path: '/compare' }, { name: c.competitor, path }]} />

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-[#2C5F66] text-3xl md:text-5xl font-extrabold leading-tight">{c.title}</h1>
          <p className="text-gray-600 text-lg leading-relaxed mt-5">{c.intro}</p>
          <div className="mt-6 bg-[#D9A04A]/10 border-l-4 border-[#D9A04A] p-5 rounded-r-lg">
            <div className="flex items-start gap-3">
              <Trophy className="text-[#D9A04A] flex-shrink-0 mt-1" size={22} />
              <div>
                <p className="text-[#2C5F66] font-bold text-sm">TL;DR</p>
                <p className="text-gray-700 text-sm mt-1">{c.winner}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
            <div className="grid grid-cols-3 bg-[#2C5F66] text-white font-bold text-sm md:text-base">
              <div className="p-4">Feature</div>
              <div className="p-4 text-center border-l border-white/20">Custom Web Design<br /><span className="text-xs text-[#D9A04A] font-normal">by Website Symphony</span></div>
              <div className="p-4 text-center border-l border-white/20">{c.competitor}</div>
            </div>
            {c.rows.map((r, idx) => (
              <div key={idx} className={`grid grid-cols-3 text-sm md:text-[15px] ${idx % 2 === 0 ? 'bg-white' : 'bg-[#F7F7F5]'}`}>
                <div className="p-4 font-semibold text-[#2C5F66]">{r.feature}</div>
                <div className="p-4 text-center border-l border-gray-100 flex items-center justify-center gap-2 text-gray-700">
                  <Check className="text-green-600 flex-shrink-0" size={16} />
                  <span>{r.us}</span>
                </div>
                <div className="p-4 text-center border-l border-gray-100 text-gray-600">{r.them}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F7F7F5]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-[#2C5F66] text-2xl md:text-3xl font-extrabold mb-4">Which is right for you?</h2>
          <p className="text-gray-600 mb-8">If your website is core to your business, custom design pays for itself. If it is a side project or you have less than $500 to invest, a builder like {c.competitor} may be the right call.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="bg-[#C68B30] hover:bg-[#B17A24] text-white font-semibold px-8 py-3 rounded-md transition-colors">Get a Free Estimate</Link>
            <Link to="/website-design-guide" className="bg-white border border-[#2C5F66] text-[#2C5F66] hover:bg-[#2C5F66] hover:text-white font-semibold px-8 py-3 rounded-md transition-colors">Read the Ultimate Guide</Link>
          </div>
        </div>
      </section>

      <ContactCTA defaultService={`Custom vs ${c.competitor}`} heading="Not sure which route to take?" subheading="Book a free consultation and we will give you an honest recommendation — even if it is not us." />
    </Layout>
  );
};

export default ComparisonPage;
